import React, { useState, useEffect } from 'react';
import { 
  Beaker, Plus, Play, Pause, Trophy, TrendingUp, AlertCircle, 
  CheckCircle, Trash2, RefreshCw, Sparkles, BarChart3, X,
  ChevronDown, ChevronRight, Target, Calendar, Activity
} from 'lucide-react';
import { formatNumber } from '../utils/formatting';
import { apiCall } from '../utils/api';

export default function ABTesting() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [expandedTestId, setExpandedTestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchTests();
    fetchCampaigns();
  }, []);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/ab-tests');
      const data = await response.json();
      
      if (data.success) {
        setTests(data.tests || []);
        setSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await apiCall('/api/campaigns');
      const data = await response.json();
      
      if (data.success) {
        setCampaigns(data.data || data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const toggleExpand = async (testId) => {
    if (expandedTestId === testId) {
      setExpandedTestId(null);
      setSelectedTest(null);
    } else {
      setExpandedTestId(testId);
      await viewTestDetails(testId);
    }
  };

  const viewTestDetails = async (testId) => {
    try {
      const response = await apiCall(`/api/ab-tests/${testId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedTest(data.test);
      }
    } catch (error) {
      console.error('Error fetching test details:', error);
    }
  };

  const applyWinner = async (testId) => {
    if (!window.confirm('Apply the winning variation and pause all losing variations?')) {
      return;
    }
    
    try {
      const response = await apiCall(`/api/ab-tests/${testId}/apply-winner`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        alert(data.message);
        fetchTests();
        if (selectedTest && selectedTest.id === testId) {
          viewTestDetails(testId);
        }
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error('Error applying winner:', error);
      alert('Failed to apply winner');
    }
  };

  const pauseTest = async (testId) => {
    try {
      const response = await apiCall(`/api/ab-tests/${testId}/pause`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        fetchTests();
        if (selectedTest && selectedTest.id === testId) {
          viewTestDetails(testId);
        }
      }
    } catch (error) {
      console.error('Error pausing test:', error);
    }
  };

  const resumeTest = async (testId) => {
    try {
      const response = await apiCall(`/api/ab-tests/${testId}/resume`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        fetchTests();
        if (selectedTest && selectedTest.id === testId) {
          viewTestDetails(testId);
        }
      }
    } catch (error) {
      console.error('Error resuming test:', error);
    }
  };

  const deleteTest = async (testId) => {
    if (!window.confirm('Delete this A/B test? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await apiCall(`/api/ab-tests/${testId}`, { method: 'DELETE' });
      const data = await response.json();
      
      if (data.success) {
        fetchTests();
        if (selectedTest && selectedTest.id === testId) {
          setSelectedTest(null);
        }
      }
    } catch (error) {
      console.error('Error deleting test:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return '#10b981';
      case 'paused': return '#f59e0b';
      case 'completed': return '#6366f1';
      case 'winner_applied': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <Play size={16} />;
      case 'paused': return <Pause size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      case 'winner_applied': return <Trophy size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={48} style={{ 
            color: '#6366f1',
            marginBottom: 20,
            animation: 'spin 1s linear infinite'
          }} />
          <p>Loading A/B tests...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 40, maxWidth: 1600, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ color: 'white', fontSize: 32, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Beaker size={36} style={{ color: '#6366f1' }} />
              A/B Testing Lab
            </h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 16 }}>
              Test ad variations and automatically apply winners
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              color: 'white',
              padding: '14px 24px',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <Plus size={20} />
            Create New Test
          </button>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 20
          }}>
            <StatCard 
              label="Total Tests"
              value={summary.total || 0}
              icon={<Beaker />}
              color="#6366f1"
            />
            <StatCard 
              label="Running"
              value={summary.running || 0}
              icon={<Play />}
              color="#10b981"
            />
            <StatCard 
              label="Completed"
              value={summary.completed || 0}
              icon={<CheckCircle />}
              color="#8b5cf6"
            />
            <StatCard 
              label="Paused"
              value={summary.paused || 0}
              icon={<Pause />}
              color="#f59e0b"
            />
          </div>
        )}
      </div>

      {/* Tests Table */}
      {tests.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '2px dashed rgba(255,255,255,0.1)',
          borderRadius: 20,
          padding: 60,
          textAlign: 'center'
        }}>
          <Beaker size={64} style={{ color: 'rgba(255,255,255,0.2)', marginBottom: 20 }} />
          <h3 style={{ color: 'white', fontSize: 24, marginBottom: 10 }}>No A/B Tests Yet</h3>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 30 }}>
            Create your first test to start optimizing your ad performance
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              color: 'white',
              padding: '14px 28px',
              borderRadius: 12,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 600
            }}
          >
            Create Your First Test
          </button>
        </div>
      ) : (
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 20,
          overflow: 'hidden'
        }}>
          {tests.map(test => (
            <TestRow
              key={test.id}
              test={test}
              isExpanded={expandedTestId === test.id}
              expandedTestDetails={expandedTestId === test.id ? selectedTest : null}
              onToggle={() => toggleExpand(test.id)}
              onPause={() => pauseTest(test.id)}
              onResume={() => resumeTest(test.id)}
              onDelete={() => deleteTest(test.id)}
              onApplyWinner={() => applyWinner(test.id)}
              onRefresh={() => viewTestDetails(test.id)}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
            />
          ))}
        </div>
      )}

      {/* Create Test Modal */}
      {showCreateModal && (
        <CreateTestModal
          campaigns={campaigns}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTests();
          }}
        />
      )}
    </div>
  );
}

// Stat Card Component
const StatCard = ({ label, value, icon, color }) => (
  <div style={{
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 16,
    padding: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 15
  }}>
    <div style={{
      width: 48,
      height: 48,
      borderRadius: 12,
      background: color + '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color
    }}>
      {React.cloneElement(icon, { size: 24 })}
    </div>
    <div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 4 }}>{label}</div>
      <div style={{ color: 'white', fontSize: 28, fontWeight: 700 }}>{value}</div>
    </div>
  </div>
);

// Test Row Component (Compact view with expandable details)
const TestRow = ({ test, isExpanded, expandedTestDetails, onToggle, onPause, onResume, onDelete, onApplyWinner, onRefresh, getStatusColor, getStatusIcon }) => {
  // Calculate winner/leader info
  const getWinnerInfo = () => {
    if (test.winner) {
      return {
        name: test.winner.name,
        lift: test.winner.lift || 0,
        confidence: test.winner.confidence
      };
    }
    // If no winner detected yet but we have expanded details, calculate from variations
    if (expandedTestDetails && expandedTestDetails.variations && expandedTestDetails.variations.length >= 2) {
      const variations = expandedTestDetails.variations;
      // Find best performing variation by CTR or conversion rate
      let bestVar = variations[0];
      let baseline = variations[0];
      
      variations.forEach(v => {
        const metric = v.metrics?.ctr || v.metrics?.conversionRate || 0;
        const bestMetric = bestVar.metrics?.ctr || bestVar.metrics?.conversionRate || 0;
        if (metric > bestMetric) {
          bestVar = v;
        }
      });
      
      const baseMetric = baseline.metrics?.ctr || baseline.metrics?.conversionRate || 0;
      const bestMetric = bestVar.metrics?.ctr || bestVar.metrics?.conversionRate || 0;
      const lift = baseMetric > 0 ? ((bestMetric - baseMetric) / baseMetric) * 100 : 0;
      
      return {
        name: bestVar.name,
        lift: lift,
        confidence: expandedTestDetails.statisticalSignificance?.confidenceLevel || 0
      };
    }
    return null;
  };

  const winnerInfo = getWinnerInfo();

  return (
    <div style={{
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      transition: 'background 0.2s'
    }}>
      {/* Compact Row */}
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 150px',
          gap: 20,
          padding: '16px 24px',
          alignItems: 'center',
          cursor: 'pointer',
          background: isExpanded ? 'rgba(99,102,241,0.08)' : 'transparent'
        }}
        onMouseEnter={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        {/* Expand Icon */}
        <div style={{ color: 'rgba(255,255,255,0.6)' }}>
          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        {/* Test Name */}
        <div>
          <div style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            {test.name}
            {test.source === 'quality_score' && (
              <span style={{
                background: 'rgba(16,185,129,0.2)', color: '#10b981',
                padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700
              }}>QS OPTIMIZER</span>
            )}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
            {test.variations?.length || 0} variations · {test.successMetric?.toUpperCase()}
            {test.adGroupName && ` · ${test.adGroupName}`}
          </div>
        </div>

        {/* Status */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: getStatusColor(test.status) + '20',
          color: getStatusColor(test.status),
          padding: '6px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          width: 'fit-content'
        }}>
          {getStatusIcon(test.status)}
          {test.status}
        </div>

        {/* Progress */}
        <div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}>
            {test.progress || 0}% Complete
          </div>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            height: 6,
            borderRadius: 3,
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#6366f1',
              height: '100%',
              width: `${test.progress || 0}%`,
              transition: 'width 0.3s'
            }} />
          </div>
        </div>

        {/* Winner/Leader */}
        <div>
          {winnerInfo ? (
            <div>
              <div style={{ color: 'white', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                {winnerInfo.name}
              </div>
              <div style={{
                color: winnerInfo.lift >= 0 ? '#10b981' : '#ef4444',
                fontSize: 12,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                <TrendingUp size={12} />
                {winnerInfo.lift >= 0 ? '+' : ''}{formatNumber(winnerInfo.lift, 1)}%
              </div>
            </div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Gathering data...
            </div>
          )}
        </div>

        {/* Winner Badge */}
        <div onClick={(e) => e.stopPropagation()}>
          {test.winner && (
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '8px 12px',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: 'white',
              fontSize: 12,
              fontWeight: 600
            }}>
              <Trophy size={14} />
              Winner
            </div>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && expandedTestDetails && (
        <div style={{
          padding: '24px',
          background: 'rgba(0,0,0,0.2)',
          borderTop: '1px solid rgba(255,255,255,0.05)'
        }}>
          <TestDetails
            test={expandedTestDetails}
            onClose={onToggle}
            onApplyWinner={onApplyWinner}
            onRefresh={onRefresh}
            onPause={onPause}
            onResume={onResume}
            onDelete={onDelete}
            isEmbedded={true}
          />
        </div>
      )}
    </div>
  );
};

// Test Card Component (OLD - no longer used)
const TestCard = ({ test, isSelected, onClick, onPause, onResume, onDelete, getStatusColor, getStatusIcon }) => (
  <div
    onClick={onClick}
    style={{
      background: isSelected ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.05)',
      border: `2px solid ${isSelected ? '#6366f1' : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 16,
      padding: 20,
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
      }
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 15 }}>
      <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, flex: 1 }}>{test.name}</h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: getStatusColor(test.status) + '20',
        color: getStatusColor(test.status),
        padding: '4px 10px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600
      }}>
        {getStatusIcon(test.status)}
        {test.status}
      </div>
    </div>

    <div style={{ marginBottom: 12 }}>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 8 }}>
        {test.variations?.length || 0} variations · {test.testType?.replace('_', ' ')}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
        Success metric: <span style={{ color: 'white', fontWeight: 600 }}>{test.successMetric?.toUpperCase()}</span>
      </div>
    </div>

    {test.winner && (
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        padding: '8px 12px',
        borderRadius: 8,
        marginBottom: 12
      }}>
        <div style={{ color: 'white', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Trophy size={14} />
          Winner: {test.winner.name} ({test.winner.confidence}% confidence)
        </div>
      </div>
    )}

    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
      {test.status === 'running' && (
        <button
          onClick={(e) => { e.stopPropagation(); onPause(); }}
          style={{
            background: 'rgba(245,158,11,0.2)',
            border: '1px solid rgba(245,158,11,0.3)',
            color: '#f59e0b',
            padding: '6px 12px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600
          }}
        >
          Pause
        </button>
      )}
      {test.status === 'paused' && (
        <button
          onClick={(e) => { e.stopPropagation(); onResume(); }}
          style={{
            background: 'rgba(16,185,129,0.2)',
            border: '1px solid rgba(16,185,129,0.3)',
            color: '#10b981',
            padding: '6px 12px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600
          }}
        >
          Resume
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        style={{
          background: 'rgba(239,68,68,0.2)',
          border: '1px solid rgba(239,68,68,0.3)',
          color: '#ef4444',
          padding: '6px 12px',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          marginLeft: 'auto'
        }}
      >
        Delete
      </button>
    </div>
  </div>
);

// Test Details Component  
const TestDetails = ({ test, onClose, onApplyWinner, onRefresh, onPause, onResume, onDelete, isEmbedded = false }) => (
  <div style={{
    background: isEmbedded ? 'transparent' : 'rgba(255,255,255,0.05)',
    borderRadius: isEmbedded ? 0 : 20,
    padding: isEmbedded ? 0 : 30,
    maxHeight: isEmbedded ? 'none' : '80vh',
    overflowY: isEmbedded ? 'visible' : 'auto'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 30 }}>
      <div>
        {!isEmbedded && <h2 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 10 }}>{test.name}</h2>}
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
          Started {new Date(test.startDate).toLocaleDateString()} · {test.daysRemaining} days remaining
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onRefresh}
          style={{
            background: 'rgba(99,102,241,0.2)',
            border: '1px solid rgba(99,102,241,0.3)',
            color: '#6366f1',
            padding: '8px 12px',
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <RefreshCw size={16} />
          {!isEmbedded && 'Refresh'}
        </button>
        {test.status === 'running' && onPause && (
          <button
            onClick={(e) => { e.stopPropagation(); onPause(); }}
            style={{
              background: 'rgba(245,158,11,0.2)',
              border: '1px solid #f59e0b',
              color: '#f59e0b',
              padding: '8px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <Pause size={16} />
            Pause
          </button>
        )}
        {test.status === 'paused' && onResume && (
          <button
            onClick={(e) => { e.stopPropagation(); onResume(); }}
            style={{
              background: 'rgba(16,185,129,0.2)',
              border: '1px solid #10b981',
              color: '#10b981',
              padding: '8px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <Play size={16} />
            Resume
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{
              background: 'rgba(239,68,68,0.2)',
              border: '1px solid #ef4444',
              color: '#ef4444',
              padding: '8px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <Trash2 size={16} />
            Delete
          </button>
        )}
        {!isEmbedded && (
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>

    {/* Progress Bar */}
    <div style={{ marginBottom: 30 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Test Progress</span>
        <span style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>{test.progress}%</span>
      </div>
      <div style={{ background: 'rgba(0,0,0,0.3)', height: 8, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
          height: '100%',
          width: `${test.progress}%`,
          transition: 'width 0.3s'
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          Min sample: {test.minSampleSize} impressions
        </span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          Ends {new Date(test.endDate).toLocaleDateString()}
        </span>
      </div>
    </div>

    {/* Winner Banner */}
    {test.winner && (
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 30
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: 'white', fontSize: 18, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Trophy size={24} />
              Winner Detected!
            </div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>
              {test.winner.name} is performing {test.winner.improvement}% better with {test.winner.confidence}% confidence
            </div>
          </div>
          {test.status !== 'winner_applied' && (
            <button
              onClick={onApplyWinner}
              style={{
                background: 'white',
                border: 'none',
                color: '#059669',
                padding: '12px 24px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              Apply Winner
            </button>
          )}
        </div>
      </div>
    )}

    {/* Variations Comparison */}
    <div style={{ marginBottom: 30 }}>
      <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Variations Performance</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        {test.variations?.map((variation, index) => (
          <VariationCard
            key={variation.variationId}
            variation={variation}
            index={index}
            isWinner={test.winner && test.winner.variationId === variation.variationId}
            successMetric={test.successMetric}
          />
        ))}
      </div>
    </div>

    {/* Statistical Significance */}
    {test.statistics && test.statistics.length > 0 && (
      <div>
        <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Statistical Analysis</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {test.statistics.map((stat, index) => (
            <div key={index} style={{
              background: stat.isSignificant ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${stat.isSignificant ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 12,
              padding: 15
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: 'white', fontWeight: 600 }}>{stat.comparison}</span>
                {stat.isSignificant && (
                  <span style={{
                    background: '#10b981',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 700
                  }}>
                    SIGNIFICANT
                  </span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 2 }}>Confidence</div>
                  <div style={{ color: 'white', fontWeight: 600 }}>{stat.confidence}%</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 2 }}>Improvement</div>
                  <div style={{ color: stat.improvement > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                    {stat.improvement > 0 ? '+' : ''}{stat.improvement}%
                  </div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 2 }}>P-Value</div>
                  <div style={{ color: 'white', fontWeight: 600 }}>{stat.pValue}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Variation Card Component
const VariationCard = ({ variation, index, isWinner, successMetric }) => {
  const metrics = variation.metrics || {};
  const ctr = metrics.impressions > 0 ? formatNumber(metrics.clicks / metrics.impressions * 100, 2) : '0,00';
  const cpa = metrics.conversions > 0 ? formatNumber(metrics.cost / metrics.conversions, 2) : '-';
  const roas = metrics.cost > 0 ? formatNumber(metrics.conversionsValue / metrics.cost, 2) : '-';

  return (
    <div style={{
      background: isWinner ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.03)',
      border: `2px solid ${isWinner ? '#10b981' : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 12,
      padding: 20
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: isWinner ? '#10b981' : '#6366f1',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700
          }}>
            {String.fromCharCode(65 + index)}
          </div>
          <h4 style={{ color: 'white', fontSize: 16, fontWeight: 600 }}>{variation.name}</h4>
        </div>
        {isWinner && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#10b981',
            fontSize: 13,
            fontWeight: 700
          }}>
            <Trophy size={16} />
            WINNER
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 15 }}>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 4 }}>Impressions</div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 700 }}>{metrics.impressions || 0}</div>
        </div>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 4 }}>Clicks</div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 700 }}>{metrics.clicks || 0}</div>
        </div>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 4 }}>CTR</div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 700 }}>{ctr}%</div>
        </div>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 4 }}>Conversions</div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 700 }}>{metrics.conversions || 0}</div>
        </div>
      </div>

      {successMetric && (
        <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Success Metric ({successMetric.toUpperCase()})</span>
            <span style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>
              {successMetric === 'ctr' && `${ctr}%`}
              {successMetric === 'conversions' && metrics.conversions}
              {successMetric === 'cpa' && `€${cpa}`}
              {successMetric === 'roas' && `${roas}x`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Test Modal Component
const CreateTestModal = ({ campaigns, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adGroups, setAdGroups] = useState([]);
  
  const [testConfig, setTestConfig] = useState({
    name: '',
    campaignId: '',
    adGroupId: '',
    testType: 'full_ad',
    successMetric: 'ctr',
    duration: 14,
    numVariations: 1,
    useAI: true
  });

  const [baseAd, setBaseAd] = useState({
    name: 'Original',
    headlines: ['', '', ''],
    descriptions: ['', ''],
    finalUrl: '',
    path1: '',
    path2: ''
  });

  const [variations, setVariations] = useState([]);
  const [loadingAd, setLoadingAd] = useState(false);

  useEffect(() => {
    if (testConfig.campaignId) {
      fetchAdGroups(testConfig.campaignId);
    }
  }, [testConfig.campaignId]);

  useEffect(() => {
    if (testConfig.adGroupId) {
      loadExistingAd();
    }
  }, [testConfig.adGroupId]);

  const fetchAdGroups = async (campaignId) => {
    try {
      const response = await apiCall(`/api/campaigns/${campaignId}/adgroups`);
      const data = await response.json();
      console.log('Ad groups response:', data);
      if (data.success) {
        setAdGroups(data.data || data.adGroups || []);
      } else {
        console.error('Ad groups fetch failed:', data.error);
        setError(data.error || 'Failed to load ad groups');
      }
    } catch (error) {
      console.error('Error fetching ad groups:', error);
      setError('Failed to load ad groups');
    }
  };

  const loadExistingAd = async () => {
    if (!testConfig.adGroupId) {
      setError('Select an ad group first');
      return;
    }
    setLoadingAd(true);
    setError(null);
    try {
      const response = await apiCall(`/api/quality-score/ad-group-details?adGroupId=${testConfig.adGroupId}&campaignId=${testConfig.campaignId}`);
      const data = await response.json();
      if (data.success && data.adGroup) {
        const ag = data.adGroup;
        const headlines = (ag.headlines || []).filter(Boolean);
        const descriptions = (ag.descriptions || []).filter(Boolean);
        if (headlines.length === 0 && descriptions.length === 0) {
          setError('No existing RSA found in this ad group');
          return;
        }
        setBaseAd({
          ...baseAd,
          name: 'Original',
          headlines: headlines.length >= 3 ? headlines : [...headlines, ...Array(3 - headlines.length).fill('')],
          descriptions: descriptions.length >= 2 ? descriptions : [...descriptions, ...Array(2 - descriptions.length).fill('')],
          finalUrl: ag.landingPageUrl || baseAd.finalUrl
        });
      } else {
        setError(data.error || 'No existing ads found in this ad group');
      }
    } catch (err) {
      setError('Could not load existing ad');
    } finally {
      setLoadingAd(false);
    }
  };

  const generateBaseAdWithAI = async () => {
    if (!testConfig.adGroupId) {
      setError('Select an ad group first');
      return;
    }
    setLoadingAd(true);
    setError(null);
    try {
      // First fetch ad group details to get keywords and existing ad data
      const detailsResp = await apiCall(`/api/quality-score/ad-group-details?adGroupId=${testConfig.adGroupId}&campaignId=${testConfig.campaignId}`);
      const detailsData = await detailsResp.json();
      
      if (!detailsData.success || !detailsData.adGroup) {
        setError('Could not load ad group data for AI generation');
        return;
      }
      
      const ag = detailsData.adGroup;
      const existingHeadlines = ag.headlines?.length > 0 ? ag.headlines : ['Ad Headline 1', 'Ad Headline 2', 'Ad Headline 3'];
      const existingDescriptions = ag.descriptions?.length > 0 ? ag.descriptions : ['Ad Description 1', 'Ad Description 2'];
      const landingUrl = ag.landingPageUrl || baseAd.finalUrl || '';
      
      // Now call the analyze endpoint to get quality components
      const analyzeResp = await apiCall('/api/quality-score/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywords: ag.keywords || [],
          headlines: existingHeadlines,
          descriptions: existingDescriptions,
          landingPageUrl: landingUrl,
          qualityScore: ag.qualityScore || 5,
          impressions: ag.impressions || 0
        })
      });
      const analyzeData = await analyzeResp.json();
      
      if (!analyzeData.success) {
        setError('Could not analyze ad group for AI generation');
        return;
      }
      
      // Now generate improvements using the analysis
      const genResp = await apiCall('/api/quality-score/generate-improvements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis: analyzeData.analysis,
          currentAd: {
            keywords: ag.keywords || [],
            headlines: existingHeadlines,
            descriptions: existingDescriptions,
            adGroupName: ag.name || 'Ad Group',
            landingPageUrl: landingUrl
          }
        })
      });
      const genData = await genResp.json();
      
      if (genData.success && genData.improvements) {
        const newHeadlines = (genData.improvements.headlines || []).slice(0, 15);
        const newDescriptions = (genData.improvements.descriptions || []).slice(0, 4);
        setBaseAd({
          ...baseAd,
          name: 'AI Generated',
          headlines: newHeadlines.length >= 3 ? newHeadlines : [...newHeadlines, ...Array(3 - newHeadlines.length).fill('')],
          descriptions: newDescriptions.length >= 2 ? newDescriptions : [...newDescriptions, ...Array(2 - newDescriptions.length).fill('')],
          finalUrl: landingUrl || baseAd.finalUrl
        });
      } else {
        setError(genData.error || 'Failed to generate ad copy');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError('Failed to generate ad copy with AI');
    } finally {
      setLoadingAd(false);
    }
  };

  const generateAIVariations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiCall('/api/ab-tests/generate-variations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseAd: baseAd,
          numVariations: testConfig.numVariations,
          testType: testConfig.testType
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVariations(data.variations);
        setStep(4);
      } else {
        setError(data.error || 'Failed to generate variations');
      }
    } catch (error) {
      setError('Failed to generate variations');
    } finally {
      setLoading(false);
    }
  };

  const createTest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiCall('/api/ab-tests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: testConfig.name,
          campaignId: testConfig.campaignId,
          adGroupId: testConfig.adGroupId,
          testType: testConfig.testType,
          successMetric: testConfig.successMetric,
          duration: testConfig.duration,
          trafficSplit: variations.map(() => Math.floor(100 / variations.length)),
          variations: variations
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onSuccess();
      } else {
        setError(data.error || 'Failed to create test');
      }
    } catch (error) {
      setError('Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <div>
            <h3 style={{ color: 'white', marginBottom: 20 }}>Step 1: Test Configuration</h3>
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                Test Name *
              </label>
              <input
                type="text"
                value={testConfig.name}
                onChange={(e) => setTestConfig({...testConfig, name: e.target.value})}
                placeholder="e.g., Holiday Campaign Headlines Test"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                Campaign *
              </label>
              <select
                value={testConfig.campaignId}
                onChange={(e) => setTestConfig({...testConfig, campaignId: e.target.value, adGroupId: ''})}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: 14
                }}
              >
                <option value="">Select campaign...</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>

            {testConfig.campaignId && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                  Ad Group *
                </label>
                <select
                  value={testConfig.adGroupId}
                  onChange={(e) => setTestConfig({...testConfig, adGroupId: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 14
                  }}
                >
                  <option value="">Select ad group...</option>
                  {adGroups.map(adGroup => (
                    <option key={adGroup.id} value={adGroup.id}>
                      {adGroup.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 20 }}>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                  Test Type
                </label>
                <select
                  value={testConfig.testType}
                  onChange={(e) => setTestConfig({...testConfig, testType: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 14
                  }}
                >
                  <option value="full_ad">Full Ad</option>
                  <option value="headlines_only">Headlines Only</option>
                  <option value="landing_page">Landing Page</option>
                </select>
              </div>

              <div>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                  Success Metric
                </label>
                <select
                  value={testConfig.successMetric}
                  onChange={(e) => setTestConfig({...testConfig, successMetric: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 14
                  }}
                >
                  <option value="ctr">CTR (Click-Through Rate)</option>
                  <option value="conversions">Conversions</option>
                  <option value="cpa">CPA (Cost Per Acquisition)</option>
                  <option value="roas">ROAS (Return on Ad Spend)</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                  Test Duration (days)
                </label>
                <input
                  type="number"
                  value={testConfig.duration}
                  onChange={(e) => setTestConfig({...testConfig, duration: parseInt(e.target.value)})}
                  min="7"
                  max="90"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>

              <div>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                  Number of Variations
                </label>
                <input
                  type="number"
                  value={testConfig.numVariations}
                  onChange={(e) => setTestConfig({...testConfig, numVariations: parseInt(e.target.value)})}
                  min="1"
                  max="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 style={{ color: 'white', marginBottom: 20 }}>Step 2: Base Ad</h3>

            {/* Quick fill buttons */}
            <div style={{
              display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap'
            }}>
              <button
                onClick={loadExistingAd}
                disabled={loadingAd || !testConfig.adGroupId}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)',
                  color: '#a5b4fc', padding: '10px 16px', borderRadius: 8,
                  cursor: !testConfig.adGroupId || loadingAd ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: !testConfig.adGroupId || loadingAd ? 0.5 : 1
                }}
              >
                <RefreshCw size={14} className={loadingAd ? 'animate-spin' : ''} />
                Load Existing Ad from Ad Group
              </button>
              <button
                onClick={generateBaseAdWithAI}
                disabled={loadingAd || !testConfig.adGroupId}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(99,102,241,0.2))',
                  border: '1px solid rgba(168,85,247,0.4)',
                  color: '#c4b5fd', padding: '10px 16px', borderRadius: 8,
                  cursor: !testConfig.adGroupId || loadingAd ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: !testConfig.adGroupId || loadingAd ? 0.5 : 1
                }}
              >
                <Sparkles size={14} />
                {loadingAd ? 'Generating...' : 'Generate Ad with AI'}
              </button>
            </div>
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 8 }}>
                Final URL *
              </label>
              <input
                type="url"
                value={baseAd.finalUrl}
                onChange={(e) => setBaseAd({...baseAd, finalUrl: e.target.value})}
                placeholder="https://example.com/landing-page"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 12 }}>
                Headlines * (minimum 3, maximum 15)
              </label>
              {baseAd.headlines.map((headline, index) => (
                <input
                  key={index}
                  type="text"
                  value={headline}
                  onChange={(e) => {
                    const newHeadlines = [...baseAd.headlines];
                    newHeadlines[index] = e.target.value;
                    setBaseAd({...baseAd, headlines: newHeadlines});
                  }}
                  placeholder={`Headline ${index + 1} (max 30 characters)`}
                  maxLength="30"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 13,
                    marginBottom: 8
                  }}
                />
              ))}
              {baseAd.headlines.length < 15 && (
                <button
                  onClick={() => setBaseAd({...baseAd, headlines: [...baseAd.headlines, '']})}
                  style={{
                    background: 'rgba(99,102,241,0.2)',
                    border: '1px solid #6366f1',
                    color: '#6366f1',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    marginTop: 5
                  }}
                >
                  + Add Headline
                </button>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 12 }}>
                Descriptions * (minimum 2, maximum 4)
              </label>
              {baseAd.descriptions.map((description, index) => (
                <textarea
                  key={index}
                  value={description}
                  onChange={(e) => {
                    const newDescriptions = [...baseAd.descriptions];
                    newDescriptions[index] = e.target.value;
                    setBaseAd({...baseAd, descriptions: newDescriptions});
                  }}
                  placeholder={`Description ${index + 1} (max 90 characters)`}
                  maxLength="90"
                  rows="2"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: 13,
                    marginBottom: 8,
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              ))}
              {baseAd.descriptions.length < 4 && (
                <button
                  onClick={() => setBaseAd({...baseAd, descriptions: [...baseAd.descriptions, '']})}
                  style={{
                    background: 'rgba(99,102,241,0.2)',
                    border: '1px solid #6366f1',
                    color: '#6366f1',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    marginTop: 5
                  }}
                >
                  + Add Description
                </button>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 style={{ color: 'white', marginBottom: 20 }}>Step 3: Generate Variations</h3>
            
            <div style={{
              background: 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 15 }}>
                <Sparkles size={24} style={{ color: '#6366f1' }} />
                <div>
                  <h4 style={{ color: 'white', marginBottom: 4 }}>AI-Powered Variations</h4>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: 0 }}>
                    Generate {testConfig.numVariations} variation{testConfig.numVariations > 1 ? 's' : ''} using AI
                  </p>
                </div>
              </div>
              <button
                onClick={generateAIVariations}
                disabled={loading}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  border: 'none',
                  color: 'white',
                  padding: '14px',
                  borderRadius: 8,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 15,
                  fontWeight: 600,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Generating...' : 'Generate with AI ✨'}
              </button>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center'
            }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, margin: 0 }}>
                Or create variations manually
              </p>
              <button
                onClick={() => {
                  // Create manual variations
                  const manualVariations = [baseAd];
                  for (let i = 0; i < testConfig.numVariations; i++) {
                    manualVariations.push({
                      name: `Variation ${i + 1}`,
                      headlines: [...baseAd.headlines],
                      descriptions: [...baseAd.descriptions],
                      finalUrl: baseAd.finalUrl,
                      path1: '',
                      path2: ''
                    });
                  }
                  setVariations(manualVariations);
                  setStep(4);
                }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  marginTop: 12
                }}
              >
                Create Manually
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h3 style={{ color: 'white', marginBottom: 20 }}>Step 4: Review & Launch</h3>
            
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 12 }}>
                Test Summary
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: 15
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Test Name:</span>
                    <div style={{ color: 'white', fontWeight: 600, marginTop: 2 }}>{testConfig.name}</div>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Duration:</span>
                    <div style={{ color: 'white', fontWeight: 600, marginTop: 2 }}>{testConfig.duration} days</div>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Success Metric:</span>
                    <div style={{ color: 'white', fontWeight: 600, marginTop: 2 }}>{testConfig.successMetric.toUpperCase()}</div>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Variations:</span>
                    <div style={{ color: 'white', fontWeight: 600, marginTop: 2 }}>{variations.length}</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 12 }}>
                Ad Variations Preview
              </div>
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {variations.map((variation, index) => {
                  const allHeadlines = variation.headlines || [];
                  const allDescriptions = variation.descriptions || [];
                  const headlineEntries = allHeadlines.map((h, hi) => ({ text: h, origIdx: hi })).filter(e => e.text);
                  const descEntries = allDescriptions.map((d, di) => ({ text: d, origIdx: di })).filter(e => e.text);
                  const isOriginal = index === 0;
                  return (
                    <div key={index} style={{
                      background: isOriginal ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.08)',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 12,
                      border: isOriginal ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(99,102,241,0.25)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                            {variation.name || `Variation ${String.fromCharCode(65 + index)}`}
                          </span>
                          {isOriginal && (
                            <span style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>CONTROL</span>
                          )}
                          {!isOriginal && (
                            <span style={{ background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>VARIANT</span>
                          )}
                        </div>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                          {headlineEntries.length} headlines · {descEntries.length} descriptions
                        </span>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Headlines</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {headlineEntries.map((entry) => (
                            <input
                              key={entry.origIdx}
                              type="text"
                              value={entry.text}
                              maxLength={30}
                              onChange={(e) => {
                                const updated = [...variations];
                                updated[index].headlines[entry.origIdx] = e.target.value;
                                setVariations([...updated]);
                              }}
                              style={{
                                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                                padding: '5px 8px', borderRadius: 4, fontSize: 12, color: 'rgba(255,255,255,0.9)',
                                outline: 'none', width: '100%'
                              }}
                              onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                              onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                            />
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Descriptions</div>
                        {descEntries.map((entry) => (
                          <textarea
                            key={entry.origIdx}
                            value={entry.text}
                            maxLength={90}
                            rows={2}
                            onChange={(e) => {
                              const updated = [...variations];
                              updated[index].descriptions[entry.origIdx] = e.target.value;
                              setVariations([...updated]);
                            }}
                            style={{
                              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                              padding: '6px 10px', borderRadius: 4, fontSize: 12, color: 'rgba(255,255,255,0.85)',
                              marginBottom: 4, width: '100%', fontFamily: 'inherit', resize: 'vertical', outline: 'none'
                            }}
                            onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                            onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              onClick={createTest}
              disabled={loading}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                color: 'white',
                padding: '16px',
                borderRadius: 12,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 16,
                fontWeight: 700,
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Creating Test...' : '🚀 Launch A/B Test'}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch(step) {
      case 1:
        return testConfig.name && testConfig.campaignId && testConfig.adGroupId;
      case 2:
        return baseAd.finalUrl && 
               baseAd.headlines.filter(h => h.length > 0).length >= 3 &&
               baseAd.descriptions.filter(d => d.length > 0).length >= 2;
      case 3:
        return true;
      case 4:
        return variations.length >= 2;
      default:
        return false;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        background: '#1a1a2e',
        borderRadius: 20,
        padding: 40,
        maxWidth: 700,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
          <div>
            <h2 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Create A/B Test</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              {[1, 2, 3, 4].map(s => (
                <div key={s} style={{
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  background: s <= step ? '#6366f1' : 'rgba(255,255,255,0.2)'
                }} />
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: 8
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            padding: 12,
            marginBottom: 20,
            color: '#ef4444',
            fontSize: 14
          }}>
            {error}
          </div>
        )}

        {renderStep()}

        <div style={{ display: 'flex', gap: 12, marginTop: 30 }}>
          {step > 1 && step < 4 && (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                padding: '12px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              ← Back
            </button>
          )}
          {step < 3 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              style={{
                flex: 1,
                background: canProceed() ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'white',
                padding: '12px',
                borderRadius: 8,
                cursor: canProceed() ? 'pointer' : 'not-allowed',
                fontSize: 14,
                fontWeight: 600,
                opacity: canProceed() ? 1 : 0.5
              }}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

